import { useEffect, useState } from "react";
import { Landmark } from "lucide-react";
import api from "../lib/api";
import BankBreakdownTables from "./BankBreakdownTables";

const COLORS = {
  "Sometido": "#3b82f6", "Pre-Aprobado": "#0891b2", "Aprobado": "#16a34a", "Firmado": "#15803d",
  "Rechazado": "#dc2626", "Desestimado": "#f59e0b", "Sin estatus": "#94a3b8",
};
const n = (v) => Number(v || 0).toLocaleString("es-DO");

export default function BankStatusSection({ filters }) {
  const [data, setData] = useState(null);
  const fkey = JSON.stringify(filters);
  const raw = filters?.campaign;
  const campaigns = (Array.isArray(raw) ? raw : String(raw || "").split(","))
    .map((c) => c.trim()).filter((c) => c && c !== "Todas");
  useEffect(() => {
    setData(null);
    api.get("/sales-performance/bank-status", { params: JSON.parse(fkey) })
      .then((r) => setData(r.data)).catch(() => setData({ submitted: 0, statuses: [] }));
  }, [fkey]);

  return (
    <section className="surface p-5 sm:p-6 mb-5" data-testid="sp-bank-status">
      <div className="flex items-center gap-2">
        <Landmark size={18} strokeWidth={2.25} style={{ color: "#0891b2" }}/>
        <h2 className="text-xl font-extrabold font-display">Estatus de Financiamientos y Seguros</h2>
      </div>
      <p className="text-sm text-gray-500 font-semibold mt-1">Oportunidades sometidas a banco y su estatus actual.</p>
      {campaigns.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="sp-bank-status-campaigns">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-400">
            {campaigns.length === 1 ? "Campaña filtrada" : "Campañas filtradas"}
          </span>
          {campaigns.map((c) => (
            <span key={c} className="px-2 py-0.5 rounded-full text-[12px] font-bold"
                  style={{ background: "#ecfeff", color: "#0e7490", border: "1px solid #a5f3fc" }}
                  data-testid={`sp-bank-status-campaign-${c}`}>{c}</span>
          ))}
        </div>
      )}
      {!data ? (
        <div className="h-24 mt-4 rounded-lg bg-gray-50 animate-pulse" data-testid="sp-bank-status-loading"/>
      ) : (
        <div className="grid gap-6 mt-5 md:grid-cols-[220px_1fr] items-start">
          <div data-testid="sp-bank-status-submitted">
            <div className="label-up text-[10px]">Sometidas a banco</div>
            <div className="font-display font-black text-[40px] leading-none mt-1" style={{ color: "#0891b2" }}>{n(data.submitted)}</div>
            <div className="text-[12px] font-bold text-gray-500 mt-1">{data.submitted_pct}% de {n(data.total_opps)} oportunidades</div>
            {data.approval_rate != null && (
              <div className="mt-4" data-testid="sp-bank-status-approval">
                <div className="label-up text-[10px]">Tasa de aprobación</div>
                <div className="font-display font-black text-[28px] leading-none mt-1 text-emerald-700">{data.approval_rate}%</div>
                <div className="text-[11px] font-bold text-gray-500 mt-1">Aprobados + Firmados sobre todo lo sometido</div>
              </div>
            )}
          </div>
          <div data-testid="sp-bank-status-breakdown">
            <div className="label-up text-[10px] mb-2">Estatus de banco</div>
            {!data.statuses.length && <div className="text-sm text-gray-400 font-semibold">Sin oportunidades sometidas para estos filtros.</div>}
            <div className="space-y-2.5">
              {data.statuses.map((s) => (
                <div key={s.name} data-testid={`sp-bank-status-row-${s.name}`}>
                  <div className="flex justify-between text-[13px] font-bold gap-3">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[s.name] || "#64748b" }}/>{s.name}</span>
                    <span className="tabular-nums">{n(s.n)}<span className="text-gray-400 font-semibold"> · {s.pct}%</span></span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 mt-1 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${s.pct}%`, background: COLORS[s.name] || "#64748b", transition: "width 400ms ease" }}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {data && <BankBreakdownTables banks={data.banks || []} units={data.units || []} filters={JSON.parse(fkey)} conMonto={data.con_monto}/>}
    </section>
  );
}
